/**
 * SlideCard.jsx — one slide in the review list, drawn for whichever viewing mode
 * is on: focus (today's full settings panel), compare (a compact bar under the
 * stage) and grid (stage, number and the reorder arrows).
 *
 * It also holds the two behaviours the whole review list shares — reordering by
 * whole panorama runs, and the undo behind a deleted slide — because
 * CarouselView already imports this module and both sides need them.
 *
 * Undo without a rebuild: the ✕ does not delete immediately. The slide stays in
 * the store and is only hidden from the list for a few seconds, so Undo is
 * simply "stop hiding it" and the slide returns in its own place with its crops,
 * look and panorama group untouched. Anything that has to see the real list —
 * an export, a reorder, leaving the page — commits the delete first.
 */

import { memo, useEffect, useRef, useSyncExternalStore } from 'react';
import {
  deleteAt, detachAt, duplicateAt, exportOne, getState, moveSlideToIndex, openEditor, openPicker,
  resetCropAt, setLayoutAt, setSplitAt, setZoomAt, swapAt, updatePhotoDims,
} from '../../hooks/useCarouselStore';
import {
  LAYOUT_NAMES, cellCount, cellImgId, cellLookAt, cellRoleShort, heroSplit, layoutName,
  slideUnits, zoomSliderMax,
} from '../../lib/carousel/geometry.js';
import { isSpreadLayout } from '../../lib/carousel/layouts.js';
import { isPlainLook, lookKey } from '../../lib/carousel/look.js';
import { getPreview, requestPreview, subscribePreviews } from '../../lib/carousel/previews.js';
import SlideStage from './SlideStage';

/* ---------------- delete, with a way back ---------------- */

export const UNDO_MS = 7000;

let pending = null;                 // { id, pos } — the slide the list is hiding
let pendingTimer = 0;
const pendingListeners = new Set();
const noPending = () => null;

function emitPending() {
  pendingListeners.forEach((fn) => { fn(); });
}

function subscribePending(fn) {
  pendingListeners.add(fn);
  return () => pendingListeners.delete(fn);
}

export const pendingDelete = () => pending;

export function usePendingDelete() {
  return useSyncExternalStore(subscribePending, pendingDelete, noPending);
}

// Resolved by id, so a reorder or another edit in the meantime cannot delete the
// wrong slide.
export function commitPendingDelete() {
  if (!pending) return;
  const { id } = pending;
  clearTimeout(pendingTimer);
  pendingTimer = 0;
  pending = null;
  const si = getState().slides.findIndex((sl) => sl.id === id);
  if (si >= 0) deleteAt(si);
  emitPending();
}

export function requestDelete(id, pos) {
  commitPendingDelete();
  pending = { id, pos };
  pendingTimer = setTimeout(commitPendingDelete, UNDO_MS);
  emitPending();
}

export function undoDelete() {
  if (!pending) return;
  clearTimeout(pendingTimer);
  pendingTimer = 0;
  pending = null;
  emitPending();
}

/* ---------------- reordering, by blocks ---------------- */

function unitsNow() {
  const slides = getState().slides;
  return { slides, units: slideUnits(slides) };
}

/**
 * Moves the block holding `id` in front of the block holding `beforeId`; a null
 * `beforeId` means the end of the carousel. Anchored on ids because a pending
 * delete is committed first, which shifts every index.
 */
export function moveUnitBeforeId(id, beforeId) {
  commitPendingDelete();
  const { slides, units } = unitsNow();
  const si = slides.findIndex((sl) => sl.id === id);
  if (si < 0) return;
  const from = units.findIndex((u) => si >= u.from && si <= u.to);
  let k = units.length;
  if (beforeId) {
    const bi = slides.findIndex((sl) => sl.id === beforeId);
    if (bi < 0) return;
    k = units.findIndex((u) => bi >= u.from && bi <= u.to);
  }
  if (from < 0 || k < 0 || k === from || k === from + 1) return;
  if (k < units.length) { moveSlideToIndex(si, units[k].from); return; }
  // moveSlideTo can only insert BEFORE a block, so the last slot is reached in
  // two hops: park in front of the final block, then send that block past us.
  const lastId = slides[units[units.length - 1].from].id;
  moveSlideToIndex(si, units[units.length - 1].from);
  const after = getState().slides;
  const li = after.findIndex((sl) => sl.id === lastId);
  const mi = after.findIndex((sl) => sl.id === id);
  if (li >= 0 && mi >= 0) moveSlideToIndex(li, mi);
}

// One step earlier or later, counted in blocks: a panorama passes a whole run.
export function moveUnitBy(id, d) {
  commitPendingDelete();
  const { slides, units } = unitsNow();
  const si = slides.findIndex((sl) => sl.id === id);
  if (si < 0) return;
  const from = units.findIndex((u) => si >= u.from && si <= u.to);
  const k = d < 0 ? from - 1 : from + 2;
  if (from < 0 || k < 0 || k > units.length) return;
  moveUnitBeforeId(id, k < units.length ? slides[units[k].from].id : null);
}

/* ---------------- the card ---------------- */

function ToolBtn({ label, title, onClick, disabled, cls }) {
  return (
    <button type="button" className={'btn tiny ghost' + (cls ? ' ' + cls : '')} title={title} aria-label={title} disabled={disabled} onClick={onClick}>
      {label}
    </button>
  );
}

function SlideCard({ sl, si, pos, mode, first, last, design, group, imgById }) {
  const panos = group && sl.pano ? { [sl.pano.gid]: group } : null;
  const ids = sl.cells.map((_, ci) => cellImgId(sl, ci, panos, imgById));
  // the look each cell paints with: the photo's, unless this slide opted out
  const looks = sl.cells.map((_, ci) => {
    const im = ids[ci] ? imgById(ids[ci]) : null;
    return im ? cellLookAt(sl, im, ci) : null;
  });
  const idKey = ids.map((id, ci) => (id || '') + '@' + (looks[ci] ? lookKey(looks[ci]) : '')).join('|');
  const looksRef = useRef(looks);
  looksRef.current = looks;

  // re-render only when one of this slide's own previews lands
  const pv = useSyncExternalStore(
    subscribePreviews,
    () => ids.map((id, ci) => (id ? getPreview(id, looksRef.current[ci]) || '' : '')).join('|'),
  );

  useEffect(() => {
    idKey.split('|').forEach((entry, ci) => {
      const id = entry.split('@')[0];
      const im = id ? imgById(id) : null;
      if (im) requestPreview(im, looksRef.current[ci], updatePhotoDims);
    });
  }, [idKey, imgById]);

  const nn = String(pos).padStart(2, '0');
  const compact = mode === 'compare';
  const panoLabel = 'Panorama ' + (sl.pano ? sl.pano.i + 1 : 1) + ' / ' + (group ? group.n : '?');
  // a spread carries the design's border and gap across the seam, so only a plain
  // panorama is full bleed
  const bleed = sl.pano && !isSpreadLayout(sl.layout) ? ' · full bleed' : '';
  // the four quick styles are the only ones the switcher can show as pressed, so a
  // registry layout has to name itself or the card says nothing about its shape
  const regName = !sl.pano && !LAYOUT_NAMES[sl.layout] ? layoutName(sl, null) : '';
  // Hero grids and registry layouts rotate positions; a pair swaps; one cell has
  // nothing to do. swapCells refuses on a pano part outright.
  const nCells = cellCount(sl.layout);
  const canSwap = !sl.pano && nCells > 1;
  const swapTitle = nCells > 2 ? 'Rotate photo positions' : 'Swap the two photos';
  const stage = (
    <div className="stage-wrap">
      <SlideStage sl={sl} si={si} design={design} group={group} imgById={imgById} interactive previews={pv} />
    </div>
  );
  const earlier = <ToolBtn label="‹" title="Move earlier" disabled={first} onClick={() => moveUnitBy(sl.id, -1)} />;
  const later = <ToolBtn label="›" title="Move later" disabled={last} onClick={() => moveUnitBy(sl.id, 1)} />;

  // Grid: a contact sheet. Everything else is one mode away, or in the editor.
  if (mode === 'grid') {
    return (
      <div className="slide cc-tile" data-si={si}>
        {stage}
        <div className="cc-tilebar">
          <span className="num">{nn}</span>
          {sl.pano && <span className="pano-tag cc-panosm" title={panoLabel + bleed}>{'Pano ' + (sl.pano.i + 1)}</span>}
          <span className="spacer" />
          {earlier}
          {later}
        </div>
      </div>
    );
  }

  return (
    <div className="slide" data-si={si}>
      {stage}
      <div className={'panel' + (compact ? ' cc-compact' : '')}>
        <div className="panel-head">
          <span className="num">{nn}</span>
          {sl.pano ? (
            <span className="pano-tag" title={bleed ? 'Panorama parts ignore the border and fill so the seams don’t show' : 'A spread carries the border and gap across the seam'}>
              {panoLabel + (compact ? '' : bleed)}
            </span>
          ) : (
            <>
              {regName && <span className="pano-tag cc-layout-tag" title="Chosen from Layouts…">{regName}</span>}
              <div className="seg">
                {Object.entries(LAYOUT_NAMES).map(([k, label]) => (
                  <button key={k} type="button" aria-pressed={sl.layout === k} onClick={() => setLayoutAt(si, k)}>{label}</button>
                ))}
              </div>
            </>
          )}
          <div className="cc-acts">
            {compact && canSwap && <ToolBtn label="⇆" title={swapTitle} onClick={() => swapAt(si)} />}
            {compact && <ToolBtn label="⤓" title="Export this slide" onClick={() => exportOne(si)} />}
            {compact ? earlier : <ToolBtn label="↑" title="Move up" disabled={first} onClick={() => moveUnitBy(sl.id, -1)} />}
            {compact ? later : <ToolBtn label="↓" title="Move down" disabled={last} onClick={() => moveUnitBy(sl.id, 1)} />}
            <ToolBtn label="⧉" title="Duplicate" onClick={() => duplicateAt(si)} />
            <ToolBtn cls="cc-danger" label="✕" title="Delete slide" onClick={() => requestDelete(sl.id, pos)} />
          </div>
        </div>

        {sl.cells.map((cell, ci) => {
          const im = ids[ci] ? imgById(ids[ci]) : null;
          return (
            <div key={ci} className="cellrow">
              {cellCount(sl.layout) > 1 && <span className="lbl role">{cellRoleShort(sl, ci)}</span>}
              <div className="cname">{im ? im.name : 'empty — drop a photo or click +'}</div>
              {im && sl.pano && ci === 0 ? (
                <>
                  {!compact && <span className="lbl">drag to reframe all parts</span>}
                  <ToolBtn label="✎" title="Rotate and adjust this photo" onClick={() => openEditor(si, ci)} />
                  <ToolBtn label="⇄" title="Use a different photo for the whole panorama" onClick={() => openPicker(si, ci)} />
                  <ToolBtn label="✂" title="Detach from the panorama" onClick={() => detachAt(si)} />
                </>
              ) : im ? (
                <>
                  {!compact && (
                    <input
                      type="range"
                      min="1"
                      max={String(zoomSliderMax(cell.zoom))}
                      step="0.01"
                      value={cell.zoom}
                      title="Zoom"
                      aria-label="Zoom"
                      onChange={(e) => setZoomAt(si, ci, parseFloat(e.target.value))}
                    />
                  )}
                  {!compact && <ToolBtn label="⟲" title="Reset crop" onClick={() => resetCropAt(si, ci)} />}
                  <ToolBtn
                    label={im && !isPlainLook(im.look) ? '✎*' : '✎'}
                    title={compact ? 'Frame, zoom, rotate and adjust' : 'Frame, rotate and adjust'}
                    onClick={() => openEditor(si, ci)}
                  />
                  <ToolBtn label="⇄" title="Replace photo" onClick={() => openPicker(si, ci)} />
                </>
              ) : (
                <ToolBtn label="+" title="Choose photo" onClick={() => openPicker(si, ci)} />
              )}
            </div>
          );
        })}

        {sl.layout === 'hero3' && (
          <div className="split-row">
            <span className="lbl">Top photo height</span>
            <input
              type="range"
              min="25"
              max="70"
              step="1"
              value={Math.round(heroSplit(sl) * 100)}
              title="Share of the slide the top photo takes"
              aria-label="Top photo height"
              onChange={(e) => setSplitAt(si, +e.target.value)}
            />
            <span className="mono">{Math.round(heroSplit(sl) * 100) + '%'}</span>
          </div>
        )}

        {!compact && canSwap && (
          <button type="button" className="btn tiny ghost" style={{ alignSelf: 'flex-start' }} onClick={() => swapAt(si)}>
            {swapTitle}
          </button>
        )}

        {!compact && (
          <button type="button" className="btn tiny" style={{ alignSelf: 'flex-start' }} onClick={() => exportOne(si)}>
            Export this slide
          </button>
        )}
      </div>
    </div>
  );
}

export default memo(SlideCard);
